import { Mat4 } from "@base/math/Mat4";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { Debug } from "@biz/Debug";
import { INetPositionOptions } from "@biz/definitions/MountDefinitons";
import { IPoleData, ISunSailData, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { AxleLogic } from "@biz/items/sunSail/logic/AxleLogic";
import { PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";

export class LogicAMCX4 {
  private static calcOppositePoleIndex(poleIndex: number): number {
    switch (poleIndex) {
      case 0:
        return 2;
      case 1:
        return 3;
      case 2:
        return 0;
      case 3:
        return 1;
    }
    return -1;
  }

  private static calcPoleOrientationFromOppositePole(_sunSailType: SunSailType, poles: IPoleData[], poleIndex: number): IVec3 {
    const p = poles[poleIndex].position;
    const o = poles[LogicAMCX4.calcOppositePoleIndex(poleIndex)].position;
    const po = Vec3.sub(o, p);
    return Vec3.normalized(Vec3.xz(po));
  }

  private static calcPoleOrientationFromAxle(_sunSailId: string, poleIndex: number, sunSail: ISunSailData): IVec3 {
    const { sunSailType } = sunSail;
    Debug.assert(!PoleLogic.isAxle(sunSailType, poleIndex), "Pole must not be an axle pole!");
    Debug.assert(AxleLogic.hasAxle(sunSailType), "SunSail has no axle!");

    const clothIndex = SunSailLogic.calcClothIndexFromPole(sunSailType, Poles.getCount(sunSail), poleIndex);
    const axleNet = SunSailLogic.calcAxleNetPositions(sunSail, clothIndex);
    Debug.notNull(axleNet);

    const approximation = SunSailLogic.calcApproximation(sunSail, poleIndex, axleNet);

    return Vec3.normalized(Vec3.xz(approximation.half));
  }

  private static calcPoleOrientationVec(sunSailId: string, poleIndex: number, sunSail: ISunSailData): IVec3 {
    const { sunSailType, poles } = sunSail;
    if (AxleLogic.hasAxle(sunSailType)) {
      if (PoleLogic.isAxle(sunSailType, poleIndex)) {
        return LogicAMCX4.calcPoleOrientationFromOppositePole(sunSailType, poles, poleIndex);
      } else {
        return LogicAMCX4.calcPoleOrientationFromAxle(sunSailId, poleIndex, sunSail);
      }
    } else {
      return LogicAMCX4.calcPoleOrientationFromOppositePole(sunSailType, poles, poleIndex);
    }
  }

  static calcGroundRotation(sunSailId: string, poleIndex: number, sunSail: ISunSailData): number {
    const orientation = LogicAMCX4.calcPoleOrientationVec(sunSailId, poleIndex, sunSail);
    return SunSailMath.calcGroundRotationFromVec(orientation);
  }

  static calcMountPositions(sunSail: ISunSailData, options?: INetPositionOptions): IVec3[] {
    const { id: sunSailId, sunSailType, positionMode } = sunSail;

    switch (positionMode || "bottom") {
      case "mount": return sunSail.poles.map((_p, i) => SunSailLogic.getPoleMountPositionModePos(sunSail, i, 0))
      case "bottom": {
        const mount: IVec3[] = [];
        const axleMount = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, 0);
        if (axleMount) {
          const indices = PoleLogic.getAxleIndices(sunSailType)!;
          mount[indices.first] = axleMount.first;
          mount[indices.second] = axleMount.second;
        }
        Debug.assert(!!axleMount, "called LogicAMCX4.calcMountPosition without axle")

        const poleCount = Poles.getCount(sunSail)
        for (let i = 0; i < poleCount; i++) {
          const pole = sunSail.poles[i];
          if (PoleLogic.isAxle(sunSailType, i)) {
            // ...already handled
          } else {
            const mountPosDelta = SunSailLogic.calcMountPosDelta(sunSail, i);
            const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, i);
            const lengthOptions = SunSailLogic.calcPoleLengthOptions(sunSail, i, "mount");
            const poleAngle = SunSailLogic.getPoleAngle(sunSail, i);
            const poleLength = SunSailLogic.getPoleLength(sunSail, i);
            const heightChangeOffset = options?.heightChangeOffsets === undefined ? 0 : (Array.isArray(options?.heightChangeOffsets) ? options?.heightChangeOffsets[i] : options?.heightChangeOffsets);
            const m = PoleHelper.calcMatrixForLengthPos(sunSailId, i, poleLength, poleAngle, poleLengthB, SunSailLogic.calcBottomPoleExtension(sunSail, i), SunSailLogic.calcPoleOffsetY(sunSail, i), -mountPosDelta.y + heightChangeOffset, lengthOptions);

            const clothIndex = SunSailLogic.calcClothIndexFromPole(sunSailType, Poles.getCount(sunSail), i);
            const axleNet = SunSailLogic.calcAxleNetPositions(sunSail, clothIndex);
            const approx = SunSailLogic.calcApproximation(sunSail, i, axleNet!);
            const rotationY = SunSailMath.calcGroundRotationFromVec(approx.half);
            const groundMatrix = SunSailLogic.calcPoleGroundMatrixFromValues(pole.position, rotationY);

            const mountPos = Vec3.applyMatrix4(Vec3.xz(mountPosDelta), Mat4.mul(groundMatrix, m));
            mount[i] = mountPos;
          }
        }
        return mount;
      }
      default: throw new Error("LogicAMCX4.calcMountPosition: PositionMode not supported so far: " + positionMode)
    }
  }
}